import { useState } from 'react';

import { Button, Card, CardBody, CardHeader, useToast } from '@/components/ui';
import { saveBlob } from '@/lib/download';
import { formatCount } from '@/lib/formatting';
import { EegRawDataUnavailable, fetchEegTimelineFile } from '../api';
import { hasTimelineFile, type SessionSummary } from '../model';

import styles from '../pages/SessionDetailPage.module.css';

/**
 * Export row for the session's timeline (`timeline_file_id`).
 *
 * The file lives in the EEG bucket as `<session>.timeline.jsonl.gz`; it is
 * fetched only when the user asks for it, gunzipped in the browser and saved
 * as plain `.jsonl` (one JSON record per line).
 */
export function TimelineExportItem({ session }: { session: SessionSummary }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (!hasTimelineFile(session)) return null;

  const fallbackName = `${session.id}.timeline.jsonl`;

  const onDownload = async () => {
    const fileId = session.timelineFileId;
    if (fileId === null) return;
    setBusy(true);
    try {
      const file = await fetchEegTimelineFile(fileId);
      const name = file.name !== '' ? file.name : fallbackName;
      const outcome = await saveBlob(
        new Blob([file.text], { type: 'application/x-ndjson;charset=utf-8' }),
        name,
        { 'application/x-ndjson': ['.jsonl'] },
      );
      if (outcome !== 'cancelled') {
        toast.success(`${name} saved · ${formatCount(file.lineCount)} records`);
      }
    } catch (error) {
      if (error instanceof EegRawDataUnavailable) {
        toast.error(error.message);
      } else {
        console.warn('Timeline download failed:', error);
        toast.error(
          !navigator.onLine ? 'No internet connection.' : 'Could not download the timeline file.',
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.exportItem}>
      <div className={styles.exportTitle}>Session timeline · {fallbackName}</div>
      <p className={styles.exportDesc}>
        The session's event timeline from the stored <code>.timeline.jsonl.gz</code>, decompressed
        to JSON Lines — one JSON record per line. Each record contains the timestamp and EEG features, including delta, theta, alpha, beta, gamma, and PLV, along with the other calculated signal metrics.
      </p>
      <span>
        <Button size="sm" icon="download" busy={busy} onClick={() => void onDownload()}>
          Download .jsonl
        </Button>
      </span>
    </div>
  );
}

/**
 * Standalone Export card holding only the timeline row — used when the raw
 * recording is not loaded, so the main Export card is not on screen.
 */
export function TimelineExportCard({ session }: { session: SessionSummary }) {
  if (!hasTimelineFile(session)) return null;
  return (
    <Card>
      <CardHeader title="Export" />
      <CardBody>
        <div className={styles.exportList}>
          <TimelineExportItem session={session} />
        </div>
      </CardBody>
    </Card>
  );
}
